import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import SearchInput from '../../shared/components/SearchInput';
import FilterBar from '../../shared/components/FilterBar';
import EmptyState from '../../shared/components/EmptyState';
import Skeleton from '../../shared/components/Skeleton';
import Pagination from '../../shared/components/Pagination';
import Table from '../../shared/components/Table';
import ClientForm from './ClientForm';
import { money, date, invoiceTotal } from '../../shared/formatters';
import StatusPill from '../../shared/components/StatusPill';
import { exportCsv, COLUMNS } from '../../shared/importExport';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../../shared/icons';

export default function ClientsList() {
  const { data } = useData();
  const navigate = useNavigate();
  const { notify } = useToast();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [overdue, setOverdue] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 220);
    return () => clearTimeout(timer);
  }, []);

  const filtered = useMemo(() => data.clients.filter((client) => {
    const clientInvoices = data.invoices.filter((invoice) => invoice.client_id === client.id);
    const hasOverdue = clientInvoices.some((invoice) => invoice.status === 'overdue' || new Date(invoice.due_date) < new Date() && invoice.status === 'sent');
    return (!query || `${client.name} ${client.email}`.toLowerCase().includes(query.toLowerCase()))
      && (status === 'all' || client.status === status)
      && (showArchived || client.status !== 'archived')
      && (!overdue || hasOverdue);
  }), [data, query, status, overdue, showArchived]);

  const pageRows = filtered.slice((page - 1) * 8, page * 8);

  if (loading) return <div className="standalone-page"><Skeleton rows={6} /></div>;

  return (
    <div className="standalone-page">
      <header className="simple-header"><div><span className="eyebrow">Customer records</span><h1>Clients</h1></div><div className="header-actions"><button className="icon-button" title="Export filtered clients (CSV)" aria-label="Export CSV" onClick={() => { exportCsv('clients', filtered, COLUMNS.clients.map((col) => ({ label: col.label, value: (row) => col.value(row) }))); notify('CSV exported'); }}><SafeIcon icon={Icons.Download} /></button><button className="primary-button" onClick={() => setForm(true)}>+ New client</button></div></header>
      <div className="toolbar"><SearchInput value={query} onChange={setQuery} placeholder="Search name or email…" /><FilterBar status={status} onStatusChange={setStatus} overdue={overdue} onOverdueChange={setOverdue} /><label className="checkbox-row toolbar-check"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived</label></div>
      {!data.clients.length ? <EmptyState title="No clients yet" action="Create client" onAction={() => setForm(true)} /> : !filtered.length ? <EmptyState title="No matching clients" message="Try changing your search or filters." /> : <><Table columns={[
        { key: 'name', label: 'Name', render: (row) => <strong>{row.name}<small>{row.email}</small></strong> },
        { key: 'status', label: 'Status', render: (row) => <StatusPill status={row.status} /> },
        { key: 'balance', label: 'Outstanding', render: (row) => money(data.invoices.filter((invoice) => invoice.client_id === row.id && invoice.status !== 'paid').reduce((sum, invoice) => sum + invoiceTotal(invoice), 0)) },
        { key: 'next', label: 'Next key date', render: (row) => date(data.keyDates.filter((item) => item.client_id === row.id).sort((a, b) => new Date(a.date) - new Date(b.date))[0]?.date) },
        { key: 'activity', label: 'Last activity', render: (row) => date(data.messages.filter((item) => item.client_id === row.id).at(-1)?.created_at) },
        { key: 'unread', label: '', render: (row) => data.messages.some((item) => item.client_id === row.id && !item.read_at) ? <span className="unread-dot" /> : null }
      ]} rows={pageRows} onRowClick={(row) => navigate(`/admin/clients/${row.id}`)} /><Pagination page={page} pages={Math.ceil(filtered.length / 8)} onChange={setPage} /></>}
      <ClientForm open={form} onClose={() => setForm(false)} onSaved={() => notify('Client saved successfully')} />
    </div>
  );
}